import React, {useEffect, useState} from 'react';
import {captureMarketingAttribution} from '../../helpers/marketingAttribution';
import {ConsentCategory, hasConsent} from '../../helpers/consent';

import {CookieStorage} from 'cookie-storage';
import {observer} from 'mobx-react';
import {useRouter} from 'next/router';

import phaseList from '../../store/structures/phaseList';
import {useStore} from '../../components/StoreProvider';

type RouterProps = {
    children?: React.ReactNode;
};

const RouterHoc: React.FC<RouterProps> = (props): JSX.Element => {
    const {} = props;
    const store = useStore();
    const router = useRouter();
    // guarantee utm fields are set before starting to log
    const [hasSetUtm, setHasSetUtm] = useState(false);

    const {progressStore} = store.uiStore;

    const findRoutIndex = (route: string) => {
        const clean = route.replace('/', '').split('?')[0].split('#')[0];
        return phaseList.findIndex((phase) => phase.route === clean);
    };

    const jumpToPhase = (phase: number) => store.uiStore.progressStore.setProgressBar({percent: 0.0, phase, slide: 0});

    router.beforePopState((e) => {
        const index = findRoutIndex(e.url);

        if (index >= 0) {
            jumpToPhase(index);
        }

        return false;
    });

    // Strip _gl (GA4 cross-domain linker) from the URL to keep browser history clean
    useEffect(() => {
        if (typeof window !== 'undefined' && window.location.search.includes('_gl=')) {
            const params = new URLSearchParams(window.location.search);
            params.delete('_gl');
            const clean = params.toString();
            const newUrl = `${window.location.pathname}${clean ? '?' + clean : ''}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, [router.asPath]);

    useEffect(() => {
        const {progressStore} = store.uiStore;

        if (progressStore.loading) progressStore.setLoading(false);
    }, [router]);

    useEffect(() => {
        // tracking pageview at hubspot
        setTimeout(() => {
            // Gated: _hsq is a queue, so pushing without consent would bank a backlog that
            // HubSpot flushes in full the moment analytics is granted.
            if (!hasConsent(ConsentCategory.analytics)) return;
            // need to put this under timeout of 700msec, otherwise the <script defer async/> will not be loaded and inturn it will not have `_hsq` in window
            const _hsq = (window._hsq = window._hsq || []);
            _hsq.push(['setPath', router.asPath]);
            _hsq.push(['trackPageView']);
        }, 700);
    }, [router.asPath]);

    useEffect(() => {
        const {progressStore} = store.uiStore;
        const cleanRoute = progressStore.route.split('?')[0].split('#')[0];
        const index = findRoutIndex(cleanRoute);
        const curIndex = findRoutIndex(router.route);

        // Preserve current query params (minus _gl) when redirecting between phases
        const params = new URLSearchParams(window.location.search);
        params.delete('_gl');
        const queryString = params.toString();
        const targetRoute = queryString ? `${cleanRoute}?${queryString}` : cleanRoute;

        if (index < curIndex) {
            router.replace(targetRoute);
        }
        if (index > curIndex) {
            router.push(targetRoute);
        }
    }, [progressStore.route]);

    const parseParam = (val) => {
        if (val == null) {
            return val;
        }
        if (Array.isArray(val)) {
            if (val.length > 0) {
                return val[0];
            }
            return null;
        }
        return val;
    };

    /**
     * Reads the attribution cookies. Written by captureMarketingAttribution above — previously
     * by GTM, which no longer runs before consent.
     */
    const getMarketingCookies = () => {
        const cookies = new CookieStorage();
        const marketingCookies = {
            campaign: null,
            medium: null,
            source: null,
            content: null,
            adgroup: null,
        };
        marketingCookies.campaign = cookies.getItem('mediaCampaign');
        marketingCookies.medium = cookies.getItem('mediaMedium');
        marketingCookies.source = cookies.getItem('mediaSource');
        marketingCookies.content = cookies.getItem('mediaContent');
        marketingCookies.adgroup = cookies.getItem('mediaAdgroup');

        return marketingCookies;
    };

    /**
     * Retrieve UTM params saved before GTM could modify the URL.
     * The inline script in _document.tsx captures the original query string
     * into sessionStorage so cross-domain linker (_gl) redirects don't lose them.
     */
    const getOriginalQueryParams = () => {
        try {
            const saved = sessionStorage.getItem('__original_query');
            if (saved) {
                sessionStorage.removeItem('__original_query');
                return new URLSearchParams(saved);
            }
        } catch (e) {}
        return null;
    };

    const handleMultiSourceParam = (primaryVal, secondaryVal) => {
        const parsedVal = parseParam(primaryVal);
        if (parsedVal) {
            return parsedVal;
        }

        return secondaryVal ? secondaryVal : '';
    };

    const {query, isReady} = router;

    useEffect(() => {
        try {
            const originalParams = getOriginalQueryParams();

            // Persist before reading, so a first visit that arrives WITH the parameters seeds the
            // cookies the next hop falls back on. GTM used to do this, but it no longer loads
            // until analytics consent, so non-consenting visitors lost their attribution.
            captureMarketingAttribution(originalParams);
            // Parsed as a URL rather than split on '?': a fragment would otherwise ride along
            // in the last value, so /basic?utm_source=newsletter#pricing stored the source as
            // "newsletter#pricing" — and these go into 90-day cookies and onto the lead.
            captureMarketingAttribution(new URL(router?.asPath || '', window.location.origin).searchParams);

            const marketingCookies = getMarketingCookies();
            const getUtmParam = (routerKey: string, originalKey: string, cookieFallback: string | null) => {
                return handleMultiSourceParam(
                    router?.query?.[routerKey] || originalParams?.get(originalKey),
                    cookieFallback,
                );
            };
            if (Object.keys(router?.query || {}).length >= 1 || originalParams) {
                store.userData.updateField('utm')({
                    campaign: getUtmParam('utm_campaign', 'utm_campaign', marketingCookies?.campaign),
                    content: parseParam(router?.query?.utm_content || originalParams?.get('utm_content')),
                    medium: getUtmParam('utm_medium', 'utm_medium', marketingCookies?.medium),
                    source: getUtmParam('utm_source', 'utm_source', marketingCookies?.source),
                    adgroup: getUtmParam('utm_adgroup', 'utm_adgroup', marketingCookies?.adgroup),
                    term: parseParam(router?.query?.utm_term || originalParams?.get('utm_term')),
                    origin: parseParam(router?.query?.utm_origin || originalParams?.get('utm_origin')),
                    subid: parseParam(router?.query?.utm_subid || originalParams?.get('utm_subid')),
                });
                store.userData.updateField('referral')({
                    rrWcid: parseParam(router?.query?.RR_WCID || originalParams?.get('RR_WCID')),
                    rrWcidTtl: parseParam(router?.query?.RR_WCID_TTL || originalParams?.get('RR_WCID_TTL')),
                    referralCode: parseParam(router?.query?.REFERRALCODE || originalParams?.get('REFERRALCODE')),
                    partnerUserId: parseParam(router?.query?.partner_user_id || originalParams?.get('partner_user_id')),
                    callbackUrl: parseParam(router?.query?.callbackUrl || originalParams?.get('callbackUrl')),
                });
                const startRef = query?.startRef || originalParams?.get('startRef');
                if (startRef) store.userData.updateField('startRef')(parseParam(startRef));
                if (
                    (store.userData.partners.tuneId === undefined || store.userData.partners.tuneId === '') &&
                    store.userData.utm.source !== 'lifestory'
                ) {
                    store.userData.updateField('partners')({
                        tuneId: parseParam(router?.query?.tune_id || originalParams?.get('tune_id')),
                        offerId: parseParam(router?.query?.offer_id || originalParams?.get('offer_id')),
                        offerName: parseParam(router?.query?.offer_name || originalParams?.get('offer_name')),
                        affiliateId: parseParam(router?.query?.affiliate_id || originalParams?.get('affiliate_id')),
                        source: parseParam(router?.query?.source || originalParams?.get('source')),
                    });
                }
            } else if (Object.values(marketingCookies).some((cookie) => !!cookie)) {
                store.userData.updateField('utm')({
                    campaign: marketingCookies?.campaign ? marketingCookies?.campaign : '',
                    medium: marketingCookies?.medium ? marketingCookies?.medium : '',
                    source: marketingCookies?.source ? marketingCookies.source : '',
                    content: marketingCookies?.content ? marketingCookies.content : '',
                    adgroup: marketingCookies?.adgroup ? marketingCookies.adgroup : '',
                });
            }

            // Fallback: partner_user_id persisted as the mediaPartnerUserId cookie on the
            // shared .getamplifylife.com domain. Covers CTAs (e.g. product pages) that hop to
            // the walkthrough WITHOUT carrying the query string, where the query/originalParams
            // branch above never runs.
            const partnerUserIdCookie = new CookieStorage().getItem('mediaPartnerUserId');
            if (!store.userData.referral.partnerUserId && partnerUserIdCookie) {
                store.userData.updateField('referral')({
                    ...store.userData.referral,
                    partnerUserId: parseParam(partnerUserIdCookie),
                });
            }
        } catch (e) {
            console.error('Error setting utm', e);
        }

        if (isReady) {
            setHasSetUtm(true);
        }
    }, [query, isReady, store]);

    return hasSetUtm && <>{props.children}</>;
};

export default observer<typeof RouterHoc>(RouterHoc);
